/**
 * Port of `HexagonalColorPicker` from react-hexagon-color-picker (MIT, crypto-red).
 * @see https://github.com/crypto-red/react-hexagon-color-picker
 */
import { memo, useId, useMemo } from 'react';

import { tryCompleteHex } from '../hexagonColorPicker.utils';
import { fillsMatch } from './compareHex';
import { getColorPathsFromHue } from './getColorPathsFromHue';

const GREY_COLOR_PATHS: { fill: string; d: string }[] = [
  { fill: '#F8F8F8', d: 'M36 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#DDD', d: 'M54 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#B2B2B2', d: 'M72 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#7F7F7F', d: 'M90 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#5F5F5F', d: 'M108 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#333', d: 'M126 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#151515', d: 'M144 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#000000', d: 'M162 240l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#EAEAEA', d: 'M45 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#C8C8C8', d: 'M63 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#969696', d: 'M81 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#777', d: 'M99 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#4D4D4D', d: 'M117 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#292929', d: 'M135 255l-9 5-9-5v-10l9-5 9 5z' },
  { fill: '#0B0B0B', d: 'M153 255l-9 5-9-5v-10l9-5 9 5z' },
];

const CURRENT_SWATCH_D = 'M213.75 243.75L198 252.5l-15.75-8.75v-17.5L198 217.5l15.75 8.75z';

export type HexagonalColorPickerProps = Omit<React.SVGProps<SVGSVGElement>, 'color' | 'onClick'> & {
  hue: number;
  /** Currently selected fill (any matching hex string). */
  color: string;
  onColorChange?: (hex: string) => void;
  onColorClick?: (event: React.MouseEvent<SVGPathElement>, hex: string) => void;
  border?: string | null;
  highlight?: string | null;
};

function emitHex(fill: string) {
  return tryCompleteHex(fill) ?? fill.trim().toLowerCase();
}

const HexagonalColorPickerBase = ({
  hue,
  color,
  onColorChange,
  onColorClick,
  border = null,
  highlight = null,
  ...svgProps
}: HexagonalColorPickerProps) => {
  const clipId = useId().replace(/:/g, '');
  const filterId = useId().replace(/:/g, '');

  const allColorPaths = useMemo(() => getColorPathsFromHue(hue), [hue]);

  const borderProps =
    border !== null
      ? {
          vectorEffect: 'non-scaling-stroke' as const,
          strokeWidth: 1,
          stroke: border,
          strokeLinejoin: 'miter' as const,
          strokeLinecap: 'square' as const,
          strokeMiterlimit: 2,
        }
      : {};

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      style={{
        isolation: 'isolate',
        pointerEvents: 'none',
        height: '100%',
        width: '100%',
      }}
      viewBox="0 0 234 260"
      width={234}
      height={260}
      {...svgProps}
    >
      <defs>
        <clipPath id={clipId}>
          <path d="M0 0h234v260H0z" />
        </clipPath>
      </defs>
      <defs>
        <filter
          id={filterId}
          x="-200%"
          y="-200%"
          width="400%"
          height="400%"
          filterUnits="objectBoundingBox"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation={2.147} />
          <feOffset result="pf_100_offsetBlur" />
          <feFlood floodColor={color} floodOpacity={0.65} />
          <feComposite in2="pf_100_offsetBlur" operator="in" result="pf_100_dropShadow" />
          <feBlend in="SourceGraphic" in2="pf_100_dropShadow" />
        </filter>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {allColorPaths.map((element, index) => {
          const isSelected = fillsMatch(element.fill, color);
          const elementBorderProps =
            isSelected && highlight !== null && highlight !== '' ? { ...borderProps, stroke: highlight } : borderProps;

          return (
            <path
              {...elementBorderProps}
              key={`all_color_paths_${index}`}
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={() => {
                onColorChange?.(emitHex(element.fill));
              }}
              fill={element.fill}
              d={element.d}
            />
          );
        })}

        {GREY_COLOR_PATHS.map((element, index) => (
          <path
            {...borderProps}
            key={`grey_color_paths_${index}`}
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onClick={() => {
              onColorChange?.(emitHex(element.fill));
            }}
            fill={element.fill}
            d={element.d}
          />
        ))}

        <g filter={`url(#${filterId})`}>
          <path
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onClick={(event) => {
              onColorClick?.(event, color);
            }}
            fill={color}
            d={CURRENT_SWATCH_D}
          />
        </g>
      </g>
    </svg>
  );
};

export const HexagonalColorPicker = memo(HexagonalColorPickerBase);
